import React, { useState } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  TouchableOpacity, 
  TextInput,
  ActivityIndicator,
  Alert,
  Platform,
  TouchableWithoutFeedback,
  Keyboard
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { sendDailySalesSummary, subscribeToNotifications, unsubscribeFromNotifications } from '../api/api';

/**
 * SMS Notification Card Component
 * 
 * @param {object} props
 * @param {number} props.clientId - The client ID
 * @param {string} props.clientName - The client name
 * @param {object} props.style - Additional style for the container
 */
const SmsNotificationCard = ({ clientId, clientName, style }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [actionResult, setActionResult] = useState({ message: '', isError: false });

  // Format phone number as user types
  const formatPhoneNumber = (text) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/[^0-9]/g, '');
    setPhoneNumber(cleaned);
  };

  // Display formatted phone number for India (assuming Indian numbers)
  const getFormattedPhoneNumber = () => {
    if (!phoneNumber) return '';
    
    // Format for E.164 display (not the actual value sent to API)
    if (phoneNumber.length > 10) {
      return `+${phoneNumber}`;
    } else if (phoneNumber.length === 10) {
      return `+91 ${phoneNumber}`;
    } else {
      return phoneNumber;
    }
  };

  // Get E.164 formatted number for API
  const getE164PhoneNumber = () => {
    if (!phoneNumber) return '';
    
    // Add India country code if it's a 10-digit number
    if (phoneNumber.length === 10) {
      return `+91${phoneNumber}`;
    } else if (phoneNumber.startsWith('+')) {
      return phoneNumber;
    } else {
      return `+${phoneNumber}`;
    }
  };

  // Send one-time SMS notification
  const handleSendSms = async () => {
    if (!validatePhoneNumber()) return;
    
    setLoading(true);
    setActionResult({ message: '', isError: false });
    
    try {
      const response = await sendDailySalesSummary(
        clientId, 
        getE164PhoneNumber()
      );
      
      setActionResult({
        message: 'Sales summary SMS sent successfully!',
        isError: false
      });
      
      // Auto-hide the success message after 5 seconds
      setTimeout(() => {
        setActionResult({ message: '', isError: false });
      }, 5000);
    } catch (error) {
      console.error('Error sending SMS:', error);
      setActionResult({
        message: error.message || 'Failed to send SMS. Please try again.',
        isError: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Subscribe to daily notifications
  const handleSubscribe = async () => {
    if (!validatePhoneNumber()) return;
    
    setLoading(true);
    setActionResult({ message: '', isError: false });
    
    try {
      const response = await subscribeToNotifications(
        clientId, 
        getE164PhoneNumber(),
        'daily'
      );
      
      setIsSubscribed(true);
      setActionResult({
        message: 'Successfully subscribed to daily sales notifications!',
        isError: false
      });
      
      // Auto-hide the success message after 5 seconds
      setTimeout(() => {
        setActionResult({ message: '', isError: false });
      }, 5000);
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      setActionResult({
        message: error.message || 'Failed to subscribe. Please try again.',
        isError: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Unsubscribe from notifications
  const handleUnsubscribe = async () => {
    setLoading(true);
    setActionResult({ message: '', isError: false });
    
    try {
      const response = await unsubscribeFromNotifications(clientId, 'daily');
      
      setIsSubscribed(false);
      setActionResult({
        message: 'Successfully unsubscribed from notifications.',
        isError: false
      });
      
      // Auto-hide the success message after 5 seconds
      setTimeout(() => {
        setActionResult({ message: '', isError: false });
      }, 5000);
    } catch (error) {
      console.error('Error unsubscribing from notifications:', error);
      setActionResult({
        message: error.message || 'Failed to unsubscribe. Please try again.',
        isError: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Validate phone number
  const validatePhoneNumber = () => {
    // Basic validation for a phone number (at least 10 digits)
    if (!phoneNumber || phoneNumber.length < 10) {
      Alert.alert(
        'Invalid Phone Number',
        'Please enter a valid phone number with at least 10 digits.'
      );
      return false;
    }
    return true;
  };

  // Toggle subscription form visibility
  const toggleForm = () => {
    setShowForm(!showForm);
    // Reset action result when toggling form
    setActionResult({ message: '', isError: false });
  };

  return (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      <View style={[styles.container, style]}>
        <View style={styles.header}>
          <View style={styles.titleContainer}>
            <Ionicons name="notifications" size={24} color="#3498db" />
            <Text style={styles.title}>SMS Notifications</Text>
          </View>
          <TouchableOpacity onPress={toggleForm} style={styles.expandButton}>
            <Ionicons 
              name={showForm ? "chevron-up" : "chevron-down"} 
              size={24} 
              color="#666"
            />
          </TouchableOpacity>
        </View>
        
        {showForm && (
          <View style={styles.formContainer}>
            <Text style={styles.description}>
              Receive sales summaries directly to your phone via SMS
            </Text>
            
            <View style={styles.inputContainer}>
              <TextInput
                style={styles.input}
                value={getFormattedPhoneNumber()}
                onChangeText={formatPhoneNumber}
                placeholder="Enter phone number"
                keyboardType="phone-pad"
                autoCompleteType="tel"
                editable={!loading}
              />
            </View>
            
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.sendButton]}
                onPress={handleSendSms}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <>
                    <Ionicons name="paper-plane" size={16} color="#fff" style={styles.buttonIcon} />
                    <Text style={styles.buttonText}>Send Now</Text>
                  </>
                )}
              </TouchableOpacity>
              
              {isSubscribed ? (
                <TouchableOpacity
                  style={[styles.button, styles.unsubscribeButton]}
                  onPress={handleUnsubscribe}
                  disabled={loading}
                >
                  {loading ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <>
                      <Ionicons name="close-circle" size={16} color="#fff" style={styles.buttonIcon} />
                      <Text style={styles.buttonText}>Unsubscribe</Text>
                    </>
                  )}
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[styles.button, styles.subscribeButton]}
                  onPress={handleSubscribe}
                  disabled={loading}
                >
                  {loading ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <>
                      <Ionicons name="checkmark-circle" size={16} color="#fff" style={styles.buttonIcon} />
                      <Text style={styles.buttonText}>Subscribe</Text>
                    </>
                  )}
                </TouchableOpacity>
              )}
            </View>
            
            {actionResult.message ? (
              <View style={[
                styles.resultMessage, 
                actionResult.isError ? styles.errorMessage : styles.successMessage
              ]}>
                <Ionicons 
                  name={actionResult.isError ? "alert-circle" : "checkmark-circle"} 
                  size={16} 
                  color={actionResult.isError ? "#e74c3c" : "#27ae60"} 
                  style={styles.messageIcon} 
                />
                <Text style={[
                  styles.messageText,
                  actionResult.isError ? styles.errorText : styles.successText
                ]}>
                  {actionResult.message}
                </Text>
              </View>
            ) : null}
          </View>
        )}
      </View>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    marginBottom: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 8,
    color: '#333',
  },
  expandButton: {
    padding: 4,
  },
  formContainer: {
    marginTop: 16,
  },
  description: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
  },
  inputContainer: {
    marginBottom: 16,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#f9f9f9',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  button: {
    flex: 1,
    borderRadius: 8,
    padding: 12,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButton: {
    backgroundColor: '#3498db',
    marginRight: 8,
  },
  subscribeButton: {
    backgroundColor: '#27ae60',
    marginLeft: 8,
  },
  unsubscribeButton: {
    backgroundColor: '#e74c3c',
    marginLeft: 8,
  },
  buttonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  buttonIcon: {
    marginRight: 6,
  },
  resultMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  errorMessage: {
    backgroundColor: '#feeef0',
  },
  successMessage: {
    backgroundColor: '#e4f7ed',
  },
  messageIcon: {
    marginRight: 8,
  },
  messageText: {
    fontSize: 14,
  },
  errorText: {
    color: '#e74c3c',
  },
  successText: {
    color: '#27ae60',
  },
});

export default SmsNotificationCard;